<template>
  <div class="filters-sidebar">
    <div class="filters-sidebar__head">
      <span class="filters-sidebar__head-title">Filtra i risultati</span>
      <button
        type="button"
        class="filters-sidebar__close"
        aria-label="Chiudi i filtri"
        @click="$emit('close')"
      >
        <vb-icon
          :name="'cross_circle'"
          :size="25"
          :color="sass('gray-600')"
        />
      </button>
    </div>

    <div class="filters-sidebar__content">
      <active-filters
        :filters="filters"
        :availability_active="!!active_filters.filter_availability"
        @filter="(payload) => $emit('filter', payload)"
        @availability="(value) => $emit('availability', value)"
        @reset="$emit('reset')"
      />

      <div class="filters-sidebar__panels">
        <filter-panel
          v-if="availability_is_known"
          class="filters-sidebar__panel filters-sidebar__panel--availability"
        >
          <availability-toggle
            :active="active_filters.filter_availability"
            :is_known="availability_is_known"
            @toggle="(value) => $emit('availability', value)"
          />
        </filter-panel>

        <filter-panel
          v-if="brand_options.length"
          class="filters-sidebar__panel filters-sidebar__panel--brand"
          :title="heading('brand')"
        >
          <brand-filter
            :options="brand_options"
            @filter="(payload) => $emit('filter', payload)"
          />
        </filter-panel>

        <filter-panel
          v-for="panel in panels"
          :id="`filters-panel-${panel.key}`"
          :key="`panel-${panel.key}`"
          class="filters-sidebar__panel"
          :class="`filters-sidebar__panel--${panel.key}`"
          :title="panel.title"
          :collapsible="panel.collapsible"
          :open="!!panels_open[panel.key]"
          :max_height="panel.max_height"
          @toggle="toggle_panel(panel.key)"
        >
          <vb-checkbox
            v-for="(option, option_index) in panel.options"
            :key="`${panel.key}-${option_index}-${update_key}`"
            v-model="option.selected"
            class="filters-sidebar__option"
            :vb_options="{
              text: (option.amount > 0
                ? `${option.text} (${option.amount})`
                : option.text
              ).toLowerCase(),
              value: option.selected,
              disabled: option.amount === 0,
              type: 'light',
            }"
            @change="() => $emit('filter', { key: panel.key })"
          />
        </filter-panel>
      </div>
    </div>

    <div class="filters-sidebar__apply">
      <vb-button
        :vb_options="{
          text: 'Cancella filtri',
          variant: 'outline-dark',
          size: 'sm',
          disabled: !has_active_filters,
        }"
        @click="$emit('reset')"
      />
      <vb-button
        class="semibold"
        :vb_options="{
          text: `Mostra ${filtered_count} risultati`,
          size: 'sm',
        }"
        @click="$emit('close')"
      />
    </div>
  </div>
</template>

<script>
  import {
    get_filter_heading,
    get_non_brand_filters,
    has_active_filters,
  } from '@alpitour/utilities/results-filters';
  import ActiveFilters from './active-filters.vue';
  import AvailabilityToggle from './availability-toggle.vue';
  import BrandFilter from './brand-filter.vue';
  import FilterPanel from './filter-panel.vue';

  const COLLAPSIBLE_PANELS = {
    keyd: 340,
  };

  export default {
    name: 'filters-sidebar',

    components: {
      'active-filters': ActiveFilters,
      'availability-toggle': AvailabilityToggle,
      'brand-filter': BrandFilter,
      'filter-panel': FilterPanel,
    },

    props: {
      filters: {
        type: Object,
        default: () => ({}),
      },
      active_filters: {
        type: Object,
        default: () => ({}),
      },
      update_key: {
        type: Number,
        default: 0,
      },
      filtered_count: {
        type: Number,
        default: 0,
      },
    },

    data() {
      return {
        panels_open: {},
      };
    },

    computed: {
      availability_is_known() {
        const { is_known = false } = this.get_availability();
        return is_known;
      },
      brand_options() {
        return this.filters.brand || [];
      },
      panels() {
        return Object.entries(get_non_brand_filters(this.filters))
          .filter(([, options]) => options.length)
          .map(([key, options]) => ({
            key,
            options,
            title: this.heading(key),
            collapsible: COLLAPSIBLE_PANELS[key] !== undefined,
            max_height: COLLAPSIBLE_PANELS[key] || 0,
          }));
      },
      has_active_filters() {
        return has_active_filters(
          this.filters,
          !!this.active_filters.filter_availability,
        );
      },
    },

    methods: {
      heading(key) {
        return get_filter_heading(key).title;
      },
      toggle_panel(key) {
        this.$set(this.panels_open, key, !this.panels_open[key]);
      },
    },
  };
</script>

<style lang="scss" scoped>
  .filters-sidebar {
    display: flex;
    flex-direction: column;
    height: 100%;

    &__head {
      display: none;
      align-items: center;
      justify-content: space-between;
      padding: $grid-gutter-width / 2 0;
      border-bottom: 1px solid $gray-200;
      min-height: 55px;

      @include media-breakpoint-down(lg) {
        display: flex;
      }
    }

    &__head-title {
      font-size: $font-size-lg;
      font-weight: $vb-font-weight-semibold;
    }

    &__close {
      display: flex;
      align-items: center;
      background: none;
      border: 0;
      padding: 0;
      cursor: pointer;
    }

    &__content {
      @include media-breakpoint-down(lg) {
        flex: 1;
        overflow-y: auto;
        padding: $grid-gutter-width / 2 0;
      }
    }

    &__panels {
      background-color: $white;
      border-radius: $border-radius-lg * 2;
      overflow: hidden;
    }

    &__option {
      display: block;
      margin-bottom: $grid-gutter-width / 6;

      &:last-child {
        margin-bottom: 0;
      }

      ::v-deep .vb-check__title {
        font-size: $font-size-md;
        text-transform: capitalize;
      }
    }

    &__apply {
      display: none;
      align-items: center;
      justify-content: space-between;
      gap: $grid-gutter-width / 3;
      background-color: $white;
      padding: $grid-gutter-width / 2;
      border-top: 1px solid $gray-200;
      box-shadow: 0 -2px 16px 0 rgba($black, 0.16);
      margin: 0 (-$grid-gutter-width / 2);

      @include media-breakpoint-down(lg) {
        display: flex;
      }
    }
  }
</style>
